import React, { useState, useEffect } from 'react';
import { View, Platform, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import ModalWithButtons from '../../ModalWithButtons';
import ModalItem from '../ModalItem';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, typography, borderRadius } from '~constants';
import Ionicons from 'react-native-vector-icons/Ionicons';

interface IProps {
  isVisible: boolean;
  close: () => void;
  onPress: (reason: string) => void;
  chosenReason?: string;
}

const ReportEventModal = ({ isVisible, close, onPress, chosenReason }: IProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isAndroid = Platform.OS === 'android';
  const { bottom } = useSafeAreaInsets();

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      paddingHorizontal: spacing.lg,
    },
    header: {
      alignItems: 'center',
      paddingTop: spacing.lg,
      paddingBottom: spacing.xl,
      borderBottomWidth: 1,
      borderBottomColor: colors.border + '30',
      marginBottom: spacing.md,
    },
    iconContainer: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: colors.error + '15',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: spacing.md,
      shadowColor: colors.error,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    },
    title: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      marginBottom: spacing.sm,
      textAlign: 'center',
      letterSpacing: 0.5,
    },
    subtitle: {
      fontSize: typography.fontSize.sm,
      textAlign: 'center',
      lineHeight: typography.fontSize.sm * 1.5,
      paddingHorizontal: spacing.lg,
      opacity: 0.8,
    },
    scrollContainer: {
      flex: 1,
      marginHorizontal: -spacing.xs,
    },
    scrollContent: {
      paddingBottom: spacing.lg,
      paddingHorizontal: spacing.xs,
    },
    itemContainer: {
      marginBottom: spacing.md,
      borderRadius: borderRadius.md,
      overflow: 'hidden',
      backgroundColor: colors.surface,
      shadowColor: colors.textPrimary,
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 1,
    },
    customItem: {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      borderRadius: borderRadius.md,
      borderWidth: 1.5,
      minHeight: 56,
    },
    itemContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    itemText: {
      fontSize: typography.fontSize.base,
      flex: 1,
      marginRight: spacing.sm,
    },
    radioButton: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    radioButtonInner: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.white,
    },
  });

  const [items, setItems] = useState([
    { title: t('report.inappropriate_content', 'Inappropriate Content'), isActive: false, reason: 'inappropriate_content' },
    { title: t('report.spam', 'Spam'), isActive: false, reason: 'spam' },
    { title: t('report.fake_event', 'Fake Event'), isActive: false, reason: 'fake_event' },
    { title: t('report.misleading_info', 'Misleading Information'), isActive: false, reason: 'misleading_info' },
    { title: t('report.harassment', 'Harassment'), isActive: false, reason: 'harassment' },
    { title: t('report.copyright', 'Copyright Violation'), isActive: false, reason: 'copyright' },
    { title: t('report.other', 'Other'), isActive: false, reason: 'other' },
  ]);

  const handleItemClick = (index: number) => {
    const updatedItems = items.map((item, i) => ({
      ...item,
      isActive: i === index,
    }));
    setItems(updatedItems);
  };

  useEffect(() => {
    if (chosenReason) {
      setItems(prevItems =>
        prevItems.map(prevItem => ({
          ...prevItem,
          isActive: prevItem.reason === chosenReason,
        }))
      );
    }
  }, [chosenReason]);

  const handleSubmit = () => {
    const selectedItem = items.find(item => item.isActive);
    if (selectedItem) {
      onPress(selectedItem.reason);
      close();
    }
  };

  const hasSelectedItem = items.some(item => item.isActive);

  return (
    <ModalWithButtons
      onCloseModal={close}
      modalIsVisible={isVisible}
      onPress={hasSelectedItem ? handleSubmit : () => {}}
      backgroundColor={colors.white}
      height="65%">
      <View style={styles.container}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Ionicons name="flag" size={24} color={colors.error} />
          </View>
          <Text style={[styles.title, { color: colors.textPrimary }]}>
            {t('report.report_event_title', 'Report Event')}
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('report.report_event_subtitle', 'Help us keep the community safe by reporting inappropriate content')}
          </Text>
        </View>

        {/* Reasons List */}
        <ScrollView 
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}>
          {items.map((item, index) => (
            <View key={index} style={styles.itemContainer}>
              <TouchableOpacity
                onPress={() => handleItemClick(index)}
                style={[
                  styles.customItem,
                  {
                    backgroundColor: item.isActive ? colors.primary + '10' : colors.surface,
                    borderColor: item.isActive ? colors.primary : colors.border + '40',
                  }
                ]}
                activeOpacity={0.7}>
                <View style={styles.itemContent}>
                  <Text style={[
                    styles.itemText, 
                    { 
                      color: item.isActive ? colors.primary : colors.textPrimary,
                      fontWeight: item.isActive ? typography.fontWeight.semibold : typography.fontWeight.medium,
                    }
                  ]}>
                    {item.title}
                  </Text>
                  <View style={[
                    styles.radioButton,
                    {
                      borderColor: item.isActive ? colors.primary : colors.border,
                      backgroundColor: item.isActive ? colors.primary : 'transparent',
                    }
                  ]}>
                    {item.isActive && <View style={styles.radioButtonInner} />}
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          ))}
        </ScrollView>
        
        {/* Bottom spacing */}
        <View style={{ height: isAndroid ? 20 : bottom + 10 }} />
      </View>
    </ModalWithButtons>
  );
};

export default ReportEventModal;
